import { SpanStatusCode, trace } from '@opentelemetry/api';
import { isRouteErrorResponse } from 'react-router';
import type { Outcome } from '~/lib/api/http.server';
import type { Logger } from '~/lib/logger';
import { notFound } from './notfound';

/**
 * Unwrap a domain `Outcome<T>` at a React Router boundary.
 *
 * **React Router native — only use inside loaders, actions, or middleware.**
 * The failure paths throw a `Response` (404 or 500) that the framework
 * detects via `isRouteErrorResponse`; `app/root.tsx`'s error boundary
 * renders the matching page. A thrown `Response` does NOT trigger
 * `entry.server.tsx`'s `handleError`, so we log and stamp the active OTel
 * span inside this function before throwing.
 *
 * Outcome handling:
 * - `ok` → returns the data.
 * - `not_found` → throws a 404 `Response` (NotFoundPage).
 * - `unavailable` / `rejected` / `already_exists` / `precondition_failed`
 *   → logs the failure with `upstream.*` fields, stamps the active span
 *   with the same attributes, then throws a 500 `Response`
 *   (UnexpectedErrorPage).
 *
 * Pass a request-scoped `logger` (typically `context.get(loggerContext)`)
 * so the log entry carries request-level fields (path, companyId, etc.).
 *
 * Domain code must keep working with `Outcome<T>` directly — calling
 * `requireOk` from outside a route layer defeats the type-safety of the
 * Outcome pattern.
 *
 * @param context Caller label, e.g. `"checkout loader: getUpcomingEvents(<id>)"`.
 * @throws {Response} 404 on `not_found`, 500 on any other non-`ok` outcome.
 */
export function requireOk<T>(
  outcome: Outcome<T>,
  context: string,
  logger: Logger
): T {
  switch (outcome.kind) {
    case 'ok':
      return outcome.data;
    case 'not_found':
      throw notFound();
    case 'unavailable':
    case 'already_exists':
    case 'precondition_failed':
      recordUpstreamFailure(context, logger, outcome);
      throw new Response(null, { status: 500 });
    case 'rejected':
      recordUpstreamFailure(context, logger, outcome);
      throw new Response(null, { status: 500 });
  }
}

/** The `error` a 503 from `requireAvailability` carries in its JSON body. */
export const AVAILABILITY_UNAVAILABLE = 'availability_unavailable';

/**
 * `requireOk` for a read of dates, times or seats. One that could not be
 * checked throws a 503 `{ error: AVAILABILITY_UNAVAILABLE }` instead of a 500,
 * so the guest can be asked to try again rather than shown an error page or a
 * calendar that looks sold out. Never cached: the next try must reach the
 * upstream.
 *
 * Only `unavailable` is answered this way. A request the upstream refused
 * (`rejected`, …) fails the same way on a retry, so it stays a 500.
 *
 * @throws {Response} 404 on `not_found`, 503 on `unavailable`, 500 otherwise.
 */
export function requireAvailability<T>(
  outcome: Outcome<T>,
  context: string,
  logger: Logger
): T {
  if (outcome.kind !== 'unavailable')
    return requireOk(outcome, context, logger);
  recordUpstreamFailure(context, logger, outcome);
  throw Response.json(
    { error: AVAILABILITY_UNAVAILABLE },
    { status: 503, headers: { 'Cache-Control': 'no-store' } }
  );
}

/** True for the error a `requireAvailability` 503 reaches an error boundary as. */
export function isAvailabilityUnavailable(error: unknown): boolean {
  return (
    isRouteErrorResponse(error) &&
    error.status === 503 &&
    (error.data as { error?: unknown } | null)?.error ===
      AVAILABILITY_UNAVAILABLE
  );
}

/**
 * Resolves to `null` where `read` rejects with the 503 `requireAvailability`
 * throws, so the caller shows the try-again notice itself. For reads inside a
 * deferred loader promise: React Router turns a Response into an error
 * response only when a loader throws it, and one rejected inside a deferred
 * promise reaches the client as an empty object, which the root error boundary
 * can't tell from any other failure.
 */
export async function catchAvailabilityUnavailable<T>(
  read: Promise<T>
): Promise<T | null> {
  try {
    return await read;
  } catch (error) {
    if (error instanceof Response && error.status === 503) {
      const body = (await error
        .clone()
        .json()
        .catch(() => null)) as { error?: unknown } | null;
      if (body?.error === AVAILABILITY_UNAVAILABLE) return null;
    }
    throw error;
  }
}

/**
 * Logs the failure and stamps the active OTel span with structured
 * `upstream.*` attributes so the 500 is searchable in Dash0. The active
 * span is whatever the React Router OTel instrumentation opened for the
 * current loader / action / middleware (see `entry.server.tsx`).
 *
 * Querying tip: in Dash0, filter on `upstream.kind` to triage by failure
 * mode, or on `upstream.context` to find a specific call site.
 */
export function recordUpstreamFailure<T>(
  context: string,
  logger: Logger,
  outcome: Outcome<T>
): void {
  const fields: Record<string, unknown> = {
    'upstream.context': context,
    'upstream.kind': outcome.kind,
  };
  if ('status' in outcome && outcome.status !== undefined)
    fields['upstream.status'] = outcome.status;
  if ('message' in outcome) fields['upstream.message'] = outcome.message;

  logger.warn(fields, `upstream failure (${outcome.kind})`);

  const span = trace.getActiveSpan();
  if (!span) return;
  span.setAttributes(fields as Record<string, string | number>);
  span.addEvent('upstream.failure', {
    context,
    kind: outcome.kind,
    ...('status' in outcome && outcome.status !== undefined
      ? { status: outcome.status }
      : {}),
    ...('message' in outcome ? { message: outcome.message } : {}),
  });
  span.setStatus({ code: SpanStatusCode.ERROR, message: outcome.kind });
}
